import { MiddlewareAPI, Dispatch, Action } from 'redux'
import morphism, { StrictSchema } from 'morphism'
import { postRequest, urlBuilder } from 'tfs.front-end-utils/requests'

import ReduxStoreInterface from '../../interfaces/client/ReduxStore'
import ClientPassengerInterface from '../../interfaces/client/Passenger'
import ServerPassengerToBeDeletedInterface from '../../interfaces/server/PassengerToBeDeleted'
import ClientPassengerToBeDeletedInterface from '../../interfaces/client/PassengerToBeDeleted'
import PassengerToBeDeletedIncludingPassengerNamesInterface from '../../interfaces/server/PassengerToBeDeletedIncludingPassengerNames'

import staticData from '../../config/staticData'

import { VALIDATE_DELETE_PASSENGERS } from '../../actions/passengers/validateDeletePassengers'
import addNotification from '../../actions/notifications/addNotification'
import setPassengersToBeDeleted from '../../actions/deletePassengers/setPassengersToBeDeleted'
import { closePassengersToBeDeletedConfirmation } from '../../actions/modals'

import passengerToBeDeletedSchema from '../../schemas/client/passengerToBeDeleted'

import getLabels from '../../utils/getLabels'
import generateAuthorizationHeaders from '../../utils/requests/generateAuthorizationHeaders'
import customTourOperatorParameters from '../../utils/tourOperators/customTourOperatorParameters'
import {
  buildServerNotification,
  dispatchErrorNotifications,
  buildGenericNotification,
} from '../../utils/notifications'

export interface ValidateDeletePassengersAction extends Action {
  passengerIds: string[]
  flightId: string
}

const formatPassenger = (
  passengerToDelete: PassengerToBeDeletedIncludingPassengerNamesInterface
) =>
  morphism<
    StrictSchema<
      ClientPassengerToBeDeletedInterface,
      PassengerToBeDeletedIncludingPassengerNamesInterface
    >
  >(passengerToBeDeletedSchema, passengerToDelete)

const getParentIdFromServerPassengerToBeDeleted = (
  serverPassengerToBeDeleted: ServerPassengerToBeDeletedInterface
) => serverPassengerToBeDeleted.id

export default (store: MiddlewareAPI) => (next: Dispatch) => async (
  action: ValidateDeletePassengersAction
) => {
  next(action)

  if (action.type !== VALIDATE_DELETE_PASSENGERS) return

  const { passengerIds, flightId } = action
  const dispatch = store.dispatch
  const state: ReduxStoreInterface = store.getState()

  const getPassengerById = (passengerId: string) =>
    state.passengers.byId[passengerId]
  const passengersFromState = state.passengers.allIds.map(getPassengerById)

  try {
    const {
      endpoints: {
        allotments: { url, headers },
      },
    } = staticData

    const fixedUrl = url.endsWith('/') ? url : `${url}/`
    const requestUrl = urlBuilder(
      `${fixedUrl}${flightId}/passengers/validate`,
      customTourOperatorParameters()
    )

    const authHeaders = await generateAuthorizationHeaders(headers)

    const validateDeleteResponse = await postRequest(requestUrl, {
      headers: { ...headers, ...authHeaders },
      body: JSON.stringify(passengerIds),
    })

    if (validateDeleteResponse.errors && validateDeleteResponse.errors.length) {
      dispatchErrorNotifications(dispatch, [
        ...validateDeleteResponse.errors,
        buildGenericNotification(
          getLabels('error_update_edit_passengers_failed_partially')
        ),
      ])

      dispatch(closePassengersToBeDeletedConfirmation())
      return
    }

    const isInfant = (
      serverPassengerToBeDeleted: ServerPassengerToBeDeletedInterface
    ) => !state.passengers.byId[serverPassengerToBeDeleted.id]

    const isParent = (
      serverPassengerToBeDeleted: ServerPassengerToBeDeletedInterface
    ) => state.passengers.byId[serverPassengerToBeDeleted.id]

    const combineParentClientAndServerData = (
      serverPassengerToBeDeleted: ServerPassengerToBeDeletedInterface
    ) => {
      const passengerFromState =
        state.passengers.byId[serverPassengerToBeDeleted.id]
      const { firstName, lastName, infant } = passengerFromState

      return {
        ...serverPassengerToBeDeleted,
        firstName,
        lastName,
        infantFirstName: infant ? infant.firstName : '',
        infantLastName: infant ? infant.lastName : '',
      }
    }

    const combineInfantClientAndServerData = (
      origin: PassengerToBeDeletedIncludingPassengerNamesInterface[],
      serverPassengerToBeDeleted: ServerPassengerToBeDeletedInterface
    ) => {
      const getParent = (passenger: ClientPassengerInterface) =>
        passenger.infant &&
        passenger.infant.id === serverPassengerToBeDeleted.id

      const parent = passengersFromState.find(getParent)

      if (!parent || parentIds.includes(parent.id) || !parent.infant) {
        return origin
      }

      const { infant } = parent

      return [
        ...origin,
        {
          ...serverPassengerToBeDeleted,
          firstName: '',
          lastName: '',
          infantFirstName: infant.firstName,
          infantLastName: infant.lastName,
        },
      ]
    }

    const parents = validateDeleteResponse.success
      .filter(isParent)
      .map(combineParentClientAndServerData)
      .map(formatPassenger)

    const parentIds = parents.map(getParentIdFromServerPassengerToBeDeleted)

    const infants = validateDeleteResponse.success
      .filter(isInfant)
      .reduce(combineInfantClientAndServerData, [])
      .map(formatPassenger)

    dispatch(setPassengersToBeDeleted([...parents, ...infants]))
  } catch (error) {
    dispatch(addNotification(buildServerNotification(error)))
    dispatch(closePassengersToBeDeletedConfirmation())
  }
}
